/**
 * AdminSettlementManagement.jsx — 관리자 정산서 관리 (어셈블리)
 *
 * 레이아웃 (3패널 PC / 탭 모바일):
 *   상단: SETTLEMENT_FEATURE 배정 마케터 칩 선택 바 + 순수익 요약
 *   좌(420px): 매장카드 (전체 정산 데이터 + 미연결 연결 UI)
 *   중(flex:1): 날짜별 트랜잭션 뷰
 *   우(300px):  정산 입금 기록 + 입력창
 *
 * 분할 구조:
 *   adminsettlement/constants.js         — C, TYPE_CFG, EMPTY_UNM_REV
 *   adminsettlement/utils.js             — fmt, fmtDate, today, contractItemsLabel, contractServiceLabel, makeToast
 *   adminsettlement/buildTimeline.js     — buildTimeline (날짜순 flat 배열)
 *   adminsettlement/pcStyles.js          — PC 레이아웃 스타일 상수
 *   adminsettlement/mobileStyles.js      — MB 모바일 스타일 객체
 *   adminsettlement/CopyTrackingButton.jsx — 송장번호 복사 버튼
 *   adminsettlement/StoreCard.jsx        — StoreCard, DetailSection
 *   adminsettlement/StoreSearchSelect.jsx — 매장 자동완성 드롭다운
 *   adminsettlement/MbComponents.jsx     — MbUnmatchedItem, MbLoadingPlaceholder
 *   adminsettlement/useAdminSettlement.js — 상태·이펙트·핸들러 훅
 *   adminsettlement/PcLeftPanel.jsx      — PC 좌 패널 (매장카드 + 미연결 연결)
 *   adminsettlement/PcCenterPanel.jsx    — PC 중 패널 (날짜순 뷰)
 *   adminsettlement/PcRightPanel.jsx     — PC 우 패널 (정산입금 폼 + 기록)
 *   adminsettlement/PcView.jsx           — PC 렌더 경로
 *   adminsettlement/MbStoresTab.jsx      — 모바일 매장카드 탭
 *   adminsettlement/MbTimelineTab.jsx    — 모바일 날짜순 탭
 *   adminsettlement/MbSettlementTab.jsx  — 모바일 정산입금 탭
 *   adminsettlement/MobileView.jsx       — 모바일 렌더 경로
 */

import React, { useState } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../hooks/useOverlayClickClose';
import { stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn } from './adminlayout/designStandard';
import { useAdminSettlement } from './adminsettlement/useAdminSettlement';
import { useMarketerHide }    from './adminsettlement/useMarketerHide';
import { PcView }             from './adminsettlement/PcView';
import { MobileView }         from './adminsettlement/MobileView';

// ── 상담자 배분율 편집 모달 ───────────────────────────────────────────────────
// contractId, currentRate(현재 적용 %), defaultRate(전역 기본값 %)
// 계약자 + 배분자 합이 항상 100%가 되도록 연동
function ShareRateModal({ modal, onSave, onReset, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [shareRate, setShareRate] = useState(String(modal.currentRate ?? modal.defaultRate ?? 50));
  const share    = Math.max(0, Math.min(100, Number(shareRate) || 0));
  const contract = 100 - share;
  const isChanged = share !== (modal.currentRate ?? modal.defaultRate);

  const ovSt = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 'max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };
  const boxSt = {
    background: 'var(--bg-card)',
    borderRadius: 14,
    width: 'min(380px, calc(100vw - 24px))',
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'min(92vh, calc(100dvh - 20px))',
    marginTop: 10,
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
    padding: '20px clamp(14px, 4vw, 24px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxSizing: 'border-box',
  };
  const labelSt = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const rowSt = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 };
  const inpSt = { border: '1.5px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 14, fontWeight: 700, width: 70, minWidth: 0, textAlign: 'right', color: 'var(--text)', background: 'var(--bg-card)', outline: 'none', boxSizing: 'border-box' };
  const valSt = { fontSize: 14, fontWeight: 700, color: 'var(--text-sub)', minWidth: 60, textAlign: 'right' };

  return (
    <div style={ovSt} {...overlayCloseProps}>
      <div style={boxSt}>
        {/* 헤더 — 검정 표준 밴드 (카드 패딩 상쇄용 음수 마진) */}
        <div style={{ ...stdModalHeaderBar(14), margin: '-20px calc(-1 * clamp(14px, 4vw, 24px)) 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>✏️</div>
            <span style={stdModalHeaderTitle}>상담배분율 편집</span>
          </div>
          <button style={stdModalCloseBtn} onClick={onClose}>✕</button>
        </div>

        {/* 계약자 % */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelSt}>계약자 (이 마케터)</label>
          <div style={rowSt}>
            <span style={{ ...valSt, color: 'var(--text)', fontSize: 16 }}>{contract}%</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>자동 계산 (100% − 배분율)</span>
          </div>
        </div>

        {/* 배분자 % — 직접 입력 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelSt}>배분자 (상담자 보유 마케터)</label>
          <div style={rowSt}>
            <input
              type="text"
              inputMode="numeric"
              style={inpSt}
              value={shareRate}
              onChange={(e) => setShareRate(e.target.value.replace(/[^0-9]/g, ''))}
            />
            <span style={{ fontSize: 14, color: 'var(--text-sub)', fontWeight: 600 }}>%</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>0~100 입력</span>
          </div>
          {/* 기본값 안내 */}
          {modal.defaultRate != null && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              전역 기본값: {modal.defaultRate}%
              {modal.currentRate != null && (
                <span style={{ marginLeft: 6, color: '#b45309', fontWeight: 600 }}>현재 수정됨</span>
              )}
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4, flexWrap: 'wrap' }}>
          {/* 기본값으로 초기화 (오버라이드가 있을 때만) */}
          {modal.currentRate != null && (
            <button
              onClick={() => onReset(modal.contractId)}
              style={{ border: '1px solid #f59e0b', borderRadius: 8, padding: '7px 14px', background: 'rgba(245,158,11,0.08)', color: '#b45309', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
            >
              기본값으로 초기화
            </button>
          )}
          <button onClick={onClose} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '7px 14px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>취소</button>
          <button
            onClick={() => isChanged && onSave(modal.contractId, share)}
            style={{ border: 'none', borderRadius: 8, padding: '7px 18px', background: isChanged ? 'linear-gradient(135deg,#1e3a8a,#2563eb)' : 'var(--bg-subtle)', color: isChanged ? '#fff' : 'var(--text-muted)', fontWeight: 700, fontSize: 13, cursor: isChanged ? 'pointer' : 'default' }}
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {string} companyCode - 정산 설정 기준 회사 코드 (기본 'PINE', LNJ 탭에서는 'LNJ')
 */
export default function AdminSettlementManagement({ companyCode = 'PINE' }) {
  const state = useAdminSettlement({ companyCode });
  const hide = useMarketerHide({ companyCode });
  const { shareRateModal, handleSaveShareRate, handleResetShareRate, closeShareRateModal } = state;

  return (
    <>
      {state.isMobile
        ? <MobileView {...state} {...hide} />
        : <PcView    {...state} {...hide} />
      }
      {/* 상담자 배분율 편집 모달 — PC/모바일 공통 최상위에서 렌더 */}
      {shareRateModal && (
        <ShareRateModal
          modal={shareRateModal}
          onSave={handleSaveShareRate}
          onReset={handleResetShareRate}
          onClose={closeShareRateModal}
        />
      )}
    </>
  );
}
